import { Controller } from "@hotwired/stimulus"

// Lightbox for recipe / smoothie gallery images. Intercepts clicks
// on each thumbnail link, opens an overlay that shows the full-size
// image with prev / next navigation. Falls back gracefully — if JS
// is disabled or the controller never connects, the thumbnail
// links still open the image in a new tab (the original MVP
// behaviour).
//
// Mount on the gallery container with data-controller=
// "gallery-lightbox". Each clickable thumbnail link must have
// data-action="click->gallery-lightbox#open" + data-full-url.
// The controller injects the overlay markup on connect, so the
// host partial doesn't need to know anything about it.
export default class extends Controller {
  static targets = [ "link" ]
  static values = {
    title: { type: String, default: "Image" }
  }

  connect() {
    this._currentIndex = 0
    this._buildOverlay()
    this._bindKeys = this._bindKeys.bind(this)
  }

  disconnect() {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay)
    }
    document.removeEventListener("keydown", this._bindKeys)
  }

  open(event) {
    event.preventDefault()
    const link = event.currentTarget
    this._currentIndex = this.linkTargets.indexOf(link)
    if (this._currentIndex < 0) this._currentIndex = 0
    this._show()
  }

  close(event) {
    if (event) event.preventDefault()
    this.overlay.dataset.open = "false"
    this._previousOverflow !== undefined && (document.body.style.overflow = this._previousOverflow)
    document.removeEventListener("keydown", this._bindKeys)
    if (this._previouslyFocused && this._previouslyFocused.focus) {
      this._previouslyFocused.focus()
    }
  }

  next(event) {
    if (event) event.preventDefault()
    if (this.linkTargets.length === 0) return
    this._currentIndex = (this._currentIndex + 1) % this.linkTargets.length
    this._renderCurrent()
  }

  prev(event) {
    if (event) event.preventDefault()
    if (this.linkTargets.length === 0) return
    this._currentIndex = (this._currentIndex - 1 + this.linkTargets.length) % this.linkTargets.length
    this._renderCurrent()
  }

  // Click on the backdrop (not the image itself) closes the
  // lightbox. We check that the click target was the overlay or
  // a designated backdrop element, not the image or controls.
  closeOnBackdrop(event) {
    if (event.target === this.overlay || event.target.dataset.lightboxBackdrop === "true") {
      this.close(event)
    }
  }

  _show() {
    this._previouslyFocused = document.activeElement
    this._previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this.overlay.dataset.open = "true"
    document.addEventListener("keydown", this._bindKeys)
    this._renderCurrent()
    requestAnimationFrame(() => this.closeButton && this.closeButton.focus())
  }

  _renderCurrent() {
    const link = this.linkTargets[this._currentIndex]
    if (!link) return

    const url = link.dataset.fullUrl || link.getAttribute("href")
    this.image.src = url
    this.image.alt = this.titleValue

    // Show / hide navigation when there's only one image.
    const showNav = this.linkTargets.length > 1
    this.prevButton.classList.toggle("hidden", !showNav)
    this.nextButton.classList.toggle("hidden", !showNav)

    if (showNav) {
      this.counter.textContent = `${this._currentIndex + 1} / ${this.linkTargets.length}`
      this.counter.classList.remove("hidden")
    } else {
      this.counter.classList.add("hidden")
    }
  }

  _bindKeys(event) {
    if (this.overlay.dataset.open !== "true") return
    switch (event.key) {
      case "Escape":     this.close(event);          break
      case "ArrowRight": this.next(event);           break
      case "ArrowLeft":  this.prev(event);           break
    }
  }

  _buildOverlay() {
    const overlay = document.createElement("div")
    overlay.className = [
      "fixed inset-0 z-50 bg-black/90 items-center justify-center",
      "hidden data-[open=true]:flex p-4"
    ].join(" ")
    overlay.dataset.open = "false"
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.dataset.lightboxBackdrop = "true"
    overlay.addEventListener("click", (e) => this.closeOnBackdrop(e))

    overlay.innerHTML = `
      <button type="button"
              data-lightbox-role="close"
              aria-label="Close"
              class="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center">
        &times;
      </button>

      <button type="button"
              data-lightbox-role="prev"
              aria-label="Previous image"
              class="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-3xl w-10 h-10 flex items-center justify-center bg-black/40 rounded-full hidden">
        &lsaquo;
      </button>

      <img data-lightbox-role="image"
           alt=""
           class="max-w-full max-h-[90vh] object-contain shadow-2xl select-none">

      <button type="button"
              data-lightbox-role="next"
              aria-label="Next image"
              class="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-3xl w-10 h-10 flex items-center justify-center bg-black/40 rounded-full hidden">
        &rsaquo;
      </button>

      <div data-lightbox-role="counter"
           class="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium hidden"
           aria-live="polite"></div>
    `

    document.body.appendChild(overlay)
    this.overlay     = overlay
    this.closeButton = overlay.querySelector("[data-lightbox-role=close]")
    this.prevButton  = overlay.querySelector("[data-lightbox-role=prev]")
    this.nextButton  = overlay.querySelector("[data-lightbox-role=next]")
    this.image       = overlay.querySelector("[data-lightbox-role=image]")
    this.counter     = overlay.querySelector("[data-lightbox-role=counter]")

    this.closeButton.addEventListener("click", (e) => this.close(e))
    this.prevButton.addEventListener("click",  (e) => this.prev(e))
    this.nextButton.addEventListener("click",  (e) => this.next(e))
  }
}
